import { runAction } from './actions';

const READY_MESSAGE = 'occ-screen-ready';
const TICKET_MESSAGE = 'occ-screen-ticket';

export type ScreenSessionHandle = {
  sessionId: string;
  launchUrl: string;
  expiresAt: Date;
  /** El navegador bloqueó la pestaña emergente: queda `launchUrl` como respaldo. */
  blocked: boolean;
};

/**
 * Abre la sesión de pantalla y le entrega el vale al lanzador sin que pase por
 * la URL (ver `packages/contracts/src/actions.ts`): el lanzador avisa por
 * `postMessage` cuando está listo para escuchar y aquí se le contesta con el
 * vale, restringido al origen exacto de `launchUrl`.
 *
 * La pestaña se abre **antes** de esperar a `screen.open`: si se abriera
 * después del `await`, ya no vendría del gesto de clic del operador y el
 * bloqueador de ventanas emergentes la cerraría.
 */
export async function openScreenSession(nodeId: string): Promise<ScreenSessionHandle> {
  const tab = window.open('', '_blank');

  const output = await runAction('screen.open', { node: { nodeId } });
  const launcherOrigin = new URL(output.launchUrl).origin;

  // No se exige `event.source === tab` cuando la pestaña quedó bloqueada: así,
  // si el operador la abre a mano con el enlace de respaldo (con `rel="opener"`,
  // ver `NodeActions.tsx`), esa pestaña nueva sigue pudiendo recibir el vale
  // aunque no sea la que se intentó abrir aquí.
  const onMessage = (event: MessageEvent) => {
    if (event.origin !== launcherOrigin) return;
    if (tab && event.source !== tab) return;
    if (
      typeof event.data !== 'object' ||
      event.data === null ||
      event.data.type !== READY_MESSAGE
    ) {
      return;
    }
    (event.source as Window).postMessage(
      { type: TICKET_MESSAGE, ticket: output.ticket },
      launcherOrigin,
    );
    window.removeEventListener('message', onMessage);
  };
  window.addEventListener('message', onMessage);

  if (tab) {
    tab.location.href = output.launchUrl;
  }

  return {
    sessionId: output.sessionId,
    launchUrl: output.launchUrl,
    expiresAt: output.expiresAt,
    blocked: tab === null || tab === undefined,
  };
}
